import type { LatLng } from '@/types/structs'

/* Ports of the geometry helpers at the bottom of custom_g_map.dart.
 *
 * The original uses TWO distance functions that happen to agree:
 * Geolocator.distanceBetween and the local _distanceInMeters — both
 * haversine with the WGS84 semi-major radius 6378137. One function here. */

const EARTH_RADIUS = 6378137.0

const degToRad = (deg: number): number => (deg * Math.PI) / 180

/** _distanceInMeters / Geolocator.distanceBetween — haversine, metres. */
export function distanceInMeters(a: LatLng, b: LatLng): number {
  const dLat = degToRad(b.lat - a.lat)
  const dLng = degToRad(b.lng - a.lng)
  const lat1 = degToRad(a.lat)
  const lat2 = degToRad(b.lat)
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  return 2 * EARTH_RADIUS * Math.asin(Math.sqrt(h))
}

function interpolate(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + (b.lat - a.lat) * t, lng: a.lng + (b.lng - a.lng) * t }
}

/* _getDottedPoints(polyline, 12) — walk-mode dotted circles.
 * ORIGINAL BUG kept: a segment shorter than stepInMeters gives steps == 0,
 * t = 0/0 = NaN, and the isFinite filter drops the WHOLE segment's points.
 * Shared endpoints of neighbouring segments duplicate; the original
 * deduplicated them accidentally via Set<CircleId(hashCode)>. */
export function getDottedPoints(polyline: LatLng[], stepInMeters: number): LatLng[] {
  const dots: LatLng[] = []
  for (let i = 0; i < polyline.length - 1; i++) {
    const start = polyline[i]!
    const end = polyline[i + 1]!
    const steps = Math.floor(distanceInMeters(start, end) / stepInMeters)
    for (let j = 0; j <= steps; j++) {
      const t = j / steps // NaN when steps == 0 — parity
      const p = interpolate(start, end, t)
      if (Number.isFinite(p.lat) && Number.isFinite(p.lng)) dots.push(p)
    }
  }
  return dots
}

/** calculateDistance — total polyline length in metres. */
export function calculateDistance(points: LatLng[]): number {
  let total = 0
  for (let i = 0; i < points.length - 1; i++) {
    total += distanceInMeters(points[i]!, points[i + 1]!)
  }
  return total
}

/* getPolylineCenter — returns the VERTEX at which accumulated length first
 * reaches half the total (not an interpolated midpoint). Falls back to the
 * last point; THROWS on an empty array, as points.last does in Dart. */
export function getPolylineCenter(points: LatLng[]): LatLng {
  const half = calculateDistance(points) / 2
  let traveled = 0
  for (let i = 0; i < points.length - 1; i++) {
    const segment = distanceInMeters(points[i]!, points[i + 1]!)
    if (traveled + segment >= half) return points[i]!
    traveled += segment
  }
  const last = points[points.length - 1]
  if (!last) throw new Error('Bad state: No element')
  return last
}

/* shiftLatLng — shifts the camera target SOUTH by shiftPx screen pixels so
 * the subject sits above the bottom sheet. Web-Mercator metres/px. */
export function shiftLatLng(center: LatLng, shiftPx: number, zoom: number): LatLng {
  const metersPerPixel =
    (156543.03392 * Math.cos((center.lat * Math.PI) / 180)) / Math.pow(2, zoom)
  const deltaLat = ((shiftPx * metersPerPixel) / EARTH_RADIUS) * (180 / Math.PI)
  return { lat: center.lat - deltaLat, lng: center.lng }
}

/** getThreshold — zoom-dependent metres for the firstPoint badge. */
export function getThreshold(zoom: number): number {
  if (zoom < 12) return 400
  if (zoom < 14) return 300
  if (zoom < 16) return 200
  if (zoom < 18) return 100
  return 50
}

/** needToShowFirstPoint — badge visible when the alarm's start point is far
 * enough from the current position marker at this zoom. */
export function needToShowFirstPoint(
  firstPoint: LatLng,
  destinationPoint: LatLng,
  zoom: number,
): boolean {
  return distanceInMeters(firstPoint, destinationPoint) > getThreshold(zoom)
}

/** _calculateDeviation — min distance from `current` to the route points. */
export function calculateDeviation(current: LatLng, points: LatLng[]): number {
  let min = Infinity
  for (const p of points) {
    const d = distanceInMeters(current, p)
    if (d < min) min = d
  }
  return min
}

/** Closest-vertex index (the _updatePolyline scan). */
export function closestPointIndex(current: LatLng, points: LatLng[]): number {
  let min = Infinity
  let idx = 0
  for (let i = 0; i < points.length; i++) {
    const d = distanceInMeters(current, points[i]!)
    if (d < min) {
      min = d
      idx = i
    }
  }
  return idx
}
